import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import validator from 'validator'
import { updateCompany, updateUserSettings } from '../../../blocks.app/user/user.state'
import { emitError } from '../../../features/appNotifications/AppNotifications.state'
import translate from '../../../core/translate'
import { toast } from 'react-toastify'
import { ICompany } from 'core/models/Company'
import { AppDispatch } from 'core/store'
import { CollapseRef } from 'blocks.simple/collapse/collapse.types'

interface Lifespan {
    systemNotification: string | number
    emailHistory: string | number
    history: string | number
    securityInfo: string | number
}

export type LifespanInputType = keyof Lifespan
export type NotificationCenterSettings = NonNullable<ICompany['notificationCenter']>

interface UseNotificationsEmailProps {
    notificationsEmail: string[]
    notificationCenter?: NotificationCenterSettings
}

interface NotificationsEmailState {
    notificationsEmailError: Record<number, boolean>
    notificationsEmails: string[]
    notificationsEmailsOriginal: string[]
    isNotificationEmailsListUpdatePending: boolean
    confirmDeleteIndex: number | null
    lifespanD: Lifespan
    editingLifespanInput: LifespanInputType | ''
    emailEditIndex: number | null
    editingEmailValue: string
    needSave: boolean
    isShowTooltip?: boolean
}

interface EditTextFocusRef {
    inputFocus: HTMLInputElement | HTMLTextAreaElement | null
}

type EmailRefKey = number | LifespanInputType

export const getInitialLifespanState = (notificationCenter?: NotificationCenterSettings): Lifespan => {
    return {
        systemNotification:
            notificationCenter && notificationCenter.systemNotificationLifespanD
                ? notificationCenter.systemNotificationLifespanD
                : '30',
        emailHistory:
            notificationCenter && notificationCenter.emailHistoryLifespanD
                ? notificationCenter.emailHistoryLifespanD
                : '30',
        history:
            notificationCenter && notificationCenter.userActionHistoryLifespanD
                ? notificationCenter.userActionHistoryLifespanD
                : '180',
        securityInfo:
            notificationCenter && notificationCenter.securityInfoMessagesLifespanD
                ? notificationCenter.securityInfoMessagesLifespanD
                : '180',
    }
}

export const useNotificationsEmail = ({ notificationsEmail, notificationCenter }: UseNotificationsEmailProps) => {
    const collapseRef = useRef<CollapseRef>({}).current
    const dispatch = useDispatch<AppDispatch>()
    const [state, setState] = useState<NotificationsEmailState>({
        notificationsEmailError: {},
        notificationsEmails: notificationsEmail,
        notificationsEmailsOriginal: notificationsEmail,
        isNotificationEmailsListUpdatePending: false,
        confirmDeleteIndex: null,
        lifespanD: getInitialLifespanState(notificationCenter),
        editingLifespanInput: '',
        emailEditIndex: null,
        editingEmailValue: '',
        needSave: false,
    })
    const emailRefs = useRef<Partial<Record<EmailRefKey, EditTextFocusRef | null>>>({})

    useEffect(() => {
        setState((prevState) => ({
            ...prevState,
            notificationsEmails: notificationsEmail,
            notificationsEmailsOriginal: notificationsEmail,
        }))
    }, [notificationsEmail])

    const validateNotificationsEmails = () => {
        const s_ = state
        let isValid = true
        const newErrors = { ...s_.notificationsEmailError }

        const hasDuplicate = s_.notificationsEmails.some(
            (value, index) => s_.notificationsEmails.indexOf(value) !== index
        )

        if (hasDuplicate) {
            isValid = false
            dispatch(emitError(translate('emailsMustBeUnique')))
        }

        s_.notificationsEmails.forEach((notificationsEmail: string, index: number) => {
            if (!validator.isEmail(notificationsEmail)) {
                newErrors[index] = true
                isValid = false
            } else {
                delete newErrors[index]
            }
        })

        setState({
            ...s_,
            notificationsEmailError: newErrors,
        })

        return isValid
    }

    const updateNotificationsEmails = (value: string, index: number) => {
        setState((s_) => {
            const errors = { ...s_.notificationsEmailError }
            delete errors[index]

            return {
                ...s_,
                notificationsEmails: s_.notificationsEmails.map((element: string, index_: number) =>
                    index === index_ ? value : element
                ),
                notificationsEmailError: errors,
            }
        })
    }

    const confirmDelete = (index: number | null) => {
        const s_ = state

        setState({
            ...s_,
            confirmDeleteIndex: index,
        })
    }

    const deleteMail = () => {
        const s_ = state

        if (s_.confirmDeleteIndex === null) return

        const notificationsEmailsAfterDelete = [...s_.notificationsEmails]
        notificationsEmailsAfterDelete.splice(s_.confirmDeleteIndex, 1)
        setState({
            ...s_,
            notificationsEmails: [...notificationsEmailsAfterDelete],
            confirmDeleteIndex: null,
        })
    }

    const addMail = () => {
        const s_ = state

        setState({
            ...s_,
            notificationsEmails: [...s_.notificationsEmails, ''],
        })
    }

    const saveEmail = () => {
        const s_ = state
        const isValid = validateNotificationsEmails()

        if (!isValid) {
            return
        }

        setState({ ...s_, isNotificationEmailsListUpdatePending: true, needSave: false })

        dispatch(updateUserSettings({ data: { notificationsEmail: s_.notificationsEmails }, isSaveSettings: true }))
        saveLifespanD(true)

        toast.info(translate('changesSavedSuccessfully'), {
            hideProgressBar: true,
            position: 'bottom-left',
        })
        if (collapseRef.onClick) {
            collapseRef.onClick()
        }
    }

    const saveLifespanD = (isSaveCompany: boolean, lifespanD: Lifespan = state.lifespanD) => {
        const notificationCenter = {
            emailHistoryLifespanD: Number(lifespanD.emailHistory),
            userActionHistoryLifespanD: Number(lifespanD.history),
            securityInfoMessagesLifespanD: Number(lifespanD.securityInfo),
            systemNotificationLifespanD: Number(lifespanD.systemNotification),
        }

        dispatch(updateCompany({ data: { notificationCenter }, isSaveCompany }))
    }

    const onChangeLifespan = (value: number, type: LifespanInputType) => {
        setState({
            ...state,
            needSave: true,
            lifespanD: {
                ...state.lifespanD,
                [type]: value,
            },
        })
    }

    const onConfirmEmailChange = () => {
        const s_ = { ...state }
        if (s_.editingLifespanInput) {
            setState({
                ...s_,
                editingLifespanInput: '',
                emailEditIndex: -1,
                needSave: false,
            })
            saveLifespanD(true, s_.lifespanD)
        } else {
            const isValid = validateNotificationsEmails()
            if (!isValid) {
                return
            }

            setState({
                ...s_,
                editingLifespanInput: '',
                needSave: true,
                emailEditIndex: -1,
            })
        }
    }

    const onCancelEmailChange = (forceEmailIndex?: number) => {
        const s_ = { ...state }
        if (s_.editingLifespanInput) {
            const editingLifespanInput = s_.editingLifespanInput
            const initialLifespan = getInitialLifespanState(notificationCenter)

            setState({
                ...s_,
                lifespanD: {
                    ...s_.lifespanD,
                    [editingLifespanInput]: initialLifespan[editingLifespanInput],
                },
                editingLifespanInput: '',
                emailEditIndex: -1,
            })
        } else {
            const emailIndex = forceEmailIndex !== undefined ? forceEmailIndex : s_.emailEditIndex
            const originalEmail =
                emailIndex === null || emailIndex < 0 ? undefined : s_.notificationsEmailsOriginal[emailIndex]
            const errors = { ...s_.notificationsEmailError }

            if (emailIndex !== null && emailIndex >= 0) {
                delete errors[emailIndex]
            }

            if (originalEmail === undefined && emailIndex !== null && emailIndex >= 0) {
                const notificationsEmails = s_.notificationsEmails.filter((_, i) => i !== emailIndex)
                setState({
                    ...s_,
                    notificationsEmails,
                    notificationsEmailError: errors,
                    editingLifespanInput: '',
                    emailEditIndex: -1,
                    needSave: true,
                })
                return
            }

            if (originalEmail !== undefined) {
                setState({
                    ...s_,
                    notificationsEmails: s_.notificationsEmails.map((email, index) =>
                        index === emailIndex ? originalEmail : email
                    ),
                    notificationsEmailError: errors,
                    editingLifespanInput: '',
                    emailEditIndex: -1,
                })
            } else {
                setState({
                    ...s_,
                    notificationsEmailError: errors,
                    editingLifespanInput: '',
                    emailEditIndex: -1,
                })
            }
        }
    }

    const handleFocus = (inputIndex: number = -1, inputType: LifespanInputType | '' = '') => {
        setState((s_) => ({
            ...s_,
            emailEditIndex: inputIndex,
            editingLifespanInput: inputType,
        }))
    }

    const focusByRefKey = (key: EmailRefKey) => {
        const editTextRef = emailRefs.current[key]
        if (editTextRef && editTextRef.inputFocus) {
            editTextRef.inputFocus.focus()
        }
    }

    return {
        state,
        updateNotificationsEmails,
        deleteMail,
        addMail,
        saveEmail,
        collapseRef,
        confirmDelete,
        saveLifespanD,
        onChangeLifespan,
        handleFocus,
        onCancelEmailChange,
        onConfirmEmailChange,
        emailRefs,
        focusByRefKey,
    }
}
